/**
 * @fileoverview Store responsável por persistir e sincronizar o estado de leads externos e do quadro CRM.
 */

import create from 'zustand';
import { persist } from 'zustand/middleware';

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

export const useExternalLeadsStore = create(
  persist(
    (set, get) => ({
      // Estado
      leads: [],
      stats: { total: 0, new: 0, pending: 0, inProgress: 0, completed: 0, unviewed: 0, byStatus: [] },
      statuses: [],
      loading: false,
      hasMore: false,
      lastFetch: null,
      initialLoadDone: false,

      // Board state
      boardLeads: {},
      boardLoading: false,
      boardLastFetch: null,

      // Actions
      setLeads: (leads) => set({ leads, lastFetch: Date.now(), initialLoadDone: true }),

      addLead: (lead) => set((state) => ({
        leads: [lead, ...state.leads.filter(l => l.id !== lead.id)],
        lastFetch: Date.now(),
        initialLoadDone: true
      })),

      updateLead: (lead) => set((state) => ({
        leads: state.leads.map(l => l.id === lead.id ? lead : l)
      })),

      deleteLead: (leadId) => set((state) => ({
        leads: state.leads.filter(l => l.id !== leadId)
      })),

      setStats: (stats) => set({ stats }),

      setStatuses: (statuses) => set({ statuses }),

      setLoading: (loading) => set({ loading }),

      setHasMore: (hasMore) => set({ hasMore }),

      setInitialLoadDone: (done) => set({ initialLoadDone: done }),

      // Board actions
      setBoardLeads: (boardLeads) => set({ boardLeads, boardLastFetch: Date.now() }),

      setBoardLoading: (boardLoading) => set({ boardLoading }),

      moveLeadToStatus: (leadId, fromStatusId, toStatusId, targetIndex) => set((state) => {
        const boardLeads = { ...state.boardLeads };
        const fromColumn = [...(boardLeads[fromStatusId] || [])];
        const toColumn = fromStatusId === toStatusId ? fromColumn : [...(boardLeads[toStatusId] || [])];

        const leadIndex = fromColumn.findIndex(l => l.id === leadId);
        if (leadIndex === -1) return state;

        const [lead] = fromColumn.splice(leadIndex, 1);
        lead.statusId = toStatusId;

        let insertAt = targetIndex != null ? targetIndex : 0;
        // Ajustar índice quando reordena na mesma coluna
        if (fromStatusId === toStatusId && targetIndex > leadIndex) {
          insertAt = Math.max(0, insertAt - 1);
        }
        toColumn.splice(insertAt, 0, lead);

        boardLeads[fromStatusId] = fromColumn;
        boardLeads[toStatusId] = toColumn;

        return { boardLeads };
      }),

      updateBoardLead: (lead) => set((state) => {
        const boardLeads = {};
        const targetStatusId = lead.statusId;
        let found = false;

        for (const [statusId, leads] of Object.entries(state.boardLeads)) {
          if (String(statusId) === String(targetStatusId)) {
            // Same column: update in place, preserving position
            const idx = leads.findIndex(l => l.id === lead.id);
            if (idx !== -1) {
              boardLeads[statusId] = leads.map(l => l.id === lead.id ? lead : l);
              found = true;
            } else {
              boardLeads[statusId] = [lead, ...leads];
              found = true;
            }
          } else {
            // Different column: remove if present
            boardLeads[statusId] = leads.filter(l => l.id !== lead.id);
          }
        }

        // If target column didn't exist yet
        if (!found) {
          if (!boardLeads[targetStatusId]) boardLeads[targetStatusId] = [];
          boardLeads[targetStatusId] = [lead, ...boardLeads[targetStatusId]];
        }

        return { boardLeads };
      }),

      addBoardLead: (lead) => set((state) => {
        const boardLeads = { ...state.boardLeads };
        const targetStatusId = lead.statusId;
        if (!boardLeads[targetStatusId]) boardLeads[targetStatusId] = [];
        boardLeads[targetStatusId] = [lead, ...boardLeads[targetStatusId].filter(l => l.id !== lead.id)];
        return { boardLeads };
      }),

      deleteBoardLead: (leadId) => set((state) => {
        const boardLeads = {};
        for (const [statusId, leads] of Object.entries(state.boardLeads)) {
          boardLeads[statusId] = leads.filter(l => l.id !== leadId);
        }
        return { boardLeads };
      }),

      clearBoardCache: () => set({ boardLeads: {}, boardLastFetch: null }),

      // Verificar se o cache ainda é válido
      isCacheValid: () => {
        const { lastFetch, leads } = get();
        if (!lastFetch || leads.length === 0) return false;
        return Date.now() - lastFetch < CACHE_DURATION;
      },

      isBoardCacheValid: () => {
        const { boardLastFetch, boardLeads } = get();
        if (!boardLastFetch || Object.keys(boardLeads).length === 0) return false;
        return Date.now() - boardLastFetch < CACHE_DURATION;
      },

      // Limpar cache (usar quando filtros mudam)
      clearCache: () => set({
        leads: [],
        lastFetch: null,
        hasMore: false,
        initialLoadDone: false
      }),

      // Reset completo
      reset: () => set({
        leads: [],
        stats: { total: 0, new: 0, pending: 0, inProgress: 0, completed: 0, unviewed: 0, byStatus: [] },
        statuses: [],
        loading: false,
        hasMore: false,
        lastFetch: null,
        initialLoadDone: false,
        boardLeads: {},
        boardLoading: false,
        boardLastFetch: null
      })
    }),
    {
      name: 'external-leads-storage',
      partialize: (state) => ({
        leads: state.leads,
        stats: state.stats,
        lastFetch: state.lastFetch,
        hasMore: state.hasMore,
        initialLoadDone: state.initialLoadDone
      }),
    }
  )
);
